<template>
  <lila-default-template>
    <template #mainContent>
      <div :class="loadingContent ? 'loading large' : ''">
        <form
          v-show="!loadingContent"
          class="container basic-info-form"
          @submit.prevent="submitForm()"
        >
          <h1 class="title">
            Novo Paciente
          </h1>

          <lila-message
            v-if="alert.location == 'page'"
            :alert-data="alert"
          />

          <div class="form-content columns">
            <div class="column is-5">
              <div class="field-group">
                <div class="field-group-header align-title">
                  <p>Paciente</p>
                </div>

                <div class="field-group-body">
                  <lila-input
                    v-model="v.data.name.$model"
                    placeholder="Nome*"
                    :validation="v.data.name"
                  />

                  <lila-input
                    v-model="v.data.registerNumber.$model"
                    placeholder="Número de Matrícula"
                    :validation="v.data.registerNumber"
                  />

                  <lila-select
                    v-model="v.data.gender.$model"
                    placeholder="Sexo*"
                    :options="genderList"
                    :validation="v.data.gender"
                  />

                  <lila-mask
                    v-model="v.data.cpf.$model"
                    placeholder="CPF*"
                    mask="###.###.###-##"
                    :validation="v.data.cpf"
                  />

                  <lila-mask
                    v-model="v.data.cns.$model"
                    placeholder="CNS*"
                    mask="### #### #### ####"
                    :validation="v.data.cns"
                  />


                  <lila-mask
                    v-model="v.data.phone.$model"
                    placeholder="Telefone*"
                    :mask="['(##) ####-####', '(##) #####-####']"
                    :validation="v.data.phone"
                  />

                  <lila-input
                    v-model="v.data.email.$model"
                    placeholder="E-mail"
                    :validation="v.data.email"
                  />

                  <div
                    class="step1-team-field"
                    :class="{ 'has-error': v.data.attendancePlaceId.$error }"
                  >
                    <div class="step1-team-picker-wrap">
                      <button
                        type="button"
                        class="step1-team-select"
                        @click.stop="teamPickerOpen = !teamPickerOpen"
                      >
                        <span :class="{ 'is-placeholder': !selectedTeamName }">
                          {{ selectedTeamName || "Unidade / Equipe*" }}
                        </span>
                        <span class="step1-team-caret" />
                      </button>
                      <div
                        v-if="teamPickerOpen"
                        class="step1-team-picker"
                      >
                        <team-tree-picker
                          :tree="teamTree"
                          :immediate="true"
                          :units-selectable="false"
                          :current-id="data.attendancePlaceId"
                          title="Selecionar unidade / equipe"
                          @select="pickTeam"
                          @cancel="teamPickerOpen = false"
                        />
                      </div>
                    </div>
                    <span
                      v-if="v.data.attendancePlaceId.$error"
                      class="step1-team-error"
                    >
                      Selecione uma unidade / equipe.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="column is-2 middle-column">
              <div class="line" />
            </div>
          </div>

          <div class="sticky-submit">
            <lila-button
              id="save-patient"
              type="submit"
              title="Salvar"
              :loading="loading"
            />
          </div>
        </form>
      </div>
    </template>
  </lila-default-template>
</template>


<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";

import Input from "@components/shared/fields/Input.vue";
import Select from "@components/shared/fields/Select.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import LilaMask from "@components/shared/fields/LilaMask.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";

import { required, email } from "@vuelidate/validators";

import GenderOptions from "@assets/options/gender.json";

const validCpf = function (value) {
  if (value) return this.$Utilities.validateCPF(value);

  return true;
};

const atLeastOne = (value, context) => {
  return !!context.cpf || !!context.cns;
};

const verifyLastName = function (value) {
  let name = value.trim().replace(/\s\s+/g, " ").split(" ");

  if (typeof name[1] == "undefined" || (typeof name[1] != "undefined" && name[1].length < 1)) return false;

  return true;
};

export default {
  components: {
    "lila-input": Input,
    "lila-select": Select,
    "lila-button": Button,
    "lila-message": Message,
    "lila-default-template": DefaultTemplate,
    "team-tree-picker": TeamTreePicker,
    LilaMask
  },
  data() {
    return {
      data: {
        id: "",
        name: "",
        gender: "",
        cpf: "",
        cns: "",
        phone: "",
        email: "",
        registerNumber: "",
        attendancePlaceId: null
      },
      genderList: GenderOptions.options,
      transferTargets: [],
      teamPickerOpen: false,
      appCodeModal: {
        active: false,
        invalidPhone: false
      },
      loading: false,
      loadingContent: false,
      sendingCode: false,
      alert: {
        message: "",
        type: "",
        show: false,
        location: ""
      }
    };
  },
  validations() {
    return {
      data: {
        name: {
          required,
          verifyLastName
        },
        gender: { required },
        cpf: {
          validCpf
        },
        cns: {},
        phone: {
          required
        },
        email: { email },
        registerNumber: {},
        attendancePlaceId: { required },
        cpfOrCns: { atLeastOne } 
      }
    };
  },
  computed: {
    selectedTeamName() {
      const id = this.data.attendancePlaceId;
      if (id == null) return "";
      const t = this.$Utilities.findTeamOrUnit(this.transferTargets, [], id);
      return t ? t.name : "";
    },
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    }
  },
  created() {
    this.getTransferTargets();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    submitForm() {
      this.alert.show = false;
      this.alert.location = "page";

      this.v.$touch();

      if (this.v.$invalid) return;

      this.loading = true;

      let patient = Object.assign({}, this.data);

      // Remove CPF ou CNS se estiver vazio
      if (!patient.cpf)
        patient.cpf = '';
      if (!patient.cns)
        patient.cns = '';


      if (patient.email == "") delete patient.email;

      patient.TeamId = patient.attendancePlaceId;
      delete patient.attendancePlaceId;

      this.$axios({
        method: "POST",
        url: "api/patient",
        data: patient,
        headers: { "Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}` }
      })
        .then(res => {
          this.data.id = res.data.Id;

          //this.openModal();
          // this.sendCode();
          this.sendCodeV2();
        })
        .catch((error) => {
          console.error("API Error:", error);  // Exibe o erro no console para depuração

          this.alert.type = 'warning';

          // Verifica se há resposta da API
          if (error.response && error.response.data) {
            // Extrai a mensagem de erro da API
            let apiMessage = error.response.data.Message || "Erro ao salvar paciente.";

            // Verifica se há um array de erros adicionais
            if (error.response.data.Errors && Array.isArray(error.response.data.Errors)) {
              const errorMessages = error.response.data.Errors.join(" ");
              this.alert.message = `${apiMessage} Detalhes: ${errorMessages}`;
            } else {
              // Caso contrário, usa apenas a mensagem da API
              this.alert.message = apiMessage;
            }

            // Exibe o alerta com a mensagem de erro
            this.$Utilities.controlAlert(this.alert.message, this.alert);
          } else if (error.message) {
            // Trata erros de rede ou outros erros sem resposta da API
            this.alert.message = error.message;
            this.$Utilities.controlAlert(this.alert.message, this.alert);
          } else {
            // Mensagem de erro genérica caso não haja outras informações
            this.alert.message = "Ocorreu um erro desconhecido.";
            this.$Utilities.controlAlert(this.alert.message, this.alert);
          }

          this.$Utilities.verifyToken(error.response ? error.response.status : null);
        })
        .finally(() => this.loading = false);
    },
    // Equipes visíveis ao profissional (api/team) com o Path das
    // unidades ancestrais, montado a partir do Parent, para agrupar a árvore.
    getTransferTargets() {
      this.loadingContent = true;

      this.$axios({
        method: "GET",
        url: "api/team",
        headers: { "Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}` }
      })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
          this.transferTargets = this.$Utilities.teamsWithPath(list);
        })
        .catch(error => this.$Utilities.controlAlert(error.response ? error.response.data : null, this.alert, "danger"))
        .finally(() => this.loadingContent = false);
    },

    // Seleção no picker (immediate): grava no form + valida + fecha.
    pickTeam(id) {
      this.data.attendancePlaceId = id;
      this.v.data.attendancePlaceId.$touch();
      this.teamPickerOpen = false;
    },

    handleClickOutside(event) {
      if (!this.teamPickerOpen) return;
      const picker = document.querySelector(".step1-team-picker");
      if (picker && !picker.contains(event.target)) {
        this.teamPickerOpen = false;
      }
    },
    openModal() {
      this.appCodeModal.active = true;
    },
    sendCodeV2() {
      this.alert.show = false;
      this.alert.location = "modal";
      this.sendingCode = true;
      this.loading = true;

      this.$axios({
        method: "POST",
        url: `api/patient/token/${this.data.id}`,
        headers: { "Authorization": `bearer ${this.$Session.get(this.$userSessionName).token.Value}` }
      })
        .then(() => {
          // Redirecionar para a rota da ficha do paciente com o ID
          this.$router.push({
            path: `/pacientes/ficha/${this.data.id}`, // Navegar para o caminho completo do paciente
            query: {
              customMessageType: "success",
              customMessage: "O paciente foi inserido com sucesso e as instruções para o uso do aplicativo foram encaminhadas por SMS e e-mail. Se desejar, você pode cadastrar informações complementares."
            }
          });
        })
        .catch(error => {
          this.$Utilities.verifyToken(error.response.status);

          if (error.response.status == 400) {
            this.appCodeModal.invalidPhone = true;
            this.$router.push({
              path: `/pacientes/ficha/${this.data.id}`,
              query: {
                customMessageType: "warning",
                customMessage: `O paciente foi inserido com sucesso, mas não foi possível enviar o SMS. Verifique se o telefone do paciente está correto.`
              }
            });
          } else {
            this.$Utilities.controlAlert(error.response.data, this.alert, "danger");
            alert.type = "danger";
            alert.show = true;
            this.appCodeModal.invalidPhone = true;
            this.$router.push({
              path: `/pacientes/ficha/${this.data.id}`,
              query: {
                customMessageType: "warning",
                customMessage: `O paciente foi inserido com sucesso, mas não foi possível enviar o SMS. Verifique se o telefone do paciente está correto.`
              }
            });
          }
        })
        .finally(() => {
          this.sendingCode = false;
          this.loading = false;
        });
    }
  }
};
</script>

<style>
@import '~@assets/css/views/complementaryRegistration.css';

/* Seletor de unidade / equipe (padrão do dropdown em árvore).
   Igual ao <lila-select> do form: altura 45, radius 45, borda branca,
   sem sombra, caret chevron #839097. */
.step1-team-picker-wrap {
  position: relative;
}

.step1-team-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 45px;
  padding: 0 20px;
  font-size: 16px;
  color: #131313;
  background: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 45px;
  box-shadow: none;
  cursor: pointer;
  font-family: inherit;
}

.step1-team-field.has-error .step1-team-select {
  border-color: #e0505f;
}

.step1-team-select .is-placeholder {
  color: #797979;
}

.step1-team-caret {
  display: block;
  width: 0.6em;
  height: 0.6em;
  border: 3px solid #839097;
  border-top: 0;
  border-right: 0;
  border-radius: 2px;
  transform: rotate(-45deg);
  margin-top: -3px;
  flex: none;
}

.step1-team-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

.step1-team-error {
  display: block;
  margin: 6px 0 0 14px;
  font-size: 12px;
  color: #e0505f;
}
</style>
